import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { RESOURCES } from "@/constants/resource-const"
import { useSearchParams } from "react-router-dom"
import BarChart from "../bar-chart/bar-chart"
import BoxplotVertical from "../boxplot/boxplot-vertical"
import { summaryStore } from "./summary-store"
import BoxplotHorizontal from "../boxplot/boxplot-horizontal"
import useIsMobile from "@/hooks/use-is-mobile"
import { statsStore } from "../../stats-store"
import { useEffect } from "react"
import LegendController from "./controller/legend-controller"
import ChartController from "./controller/chart-controller"
import { statsBundle } from "../../stats-const"
import Wrapper from "@/components/wrapper"

const SummaryView = () => {
  const [searchParams] = useSearchParams()
  const resource = searchParams.get("resource")
  const isMobile = useIsMobile()
  const { lang } = langStore()
  const { label } = RESOURCES.find((r) => r.id === resource) || { label: { ko: "", en: "" } }
  const { selectedKey, chartSwitch } = summaryStore()
  const { axisMaxMap } = statsStore()

  const getNiceMax = (value) => {
    if (!value || value <= 0) return 0

    const targetTicks = 5
    const rawStep = value / targetTicks

    const magnitude = 10 ** Math.floor(Math.log10(rawStep))
    const normalized = rawStep / magnitude

    let niceStep

    if (normalized <= 1) {
      niceStep = 1
    } else if (normalized <= 2) {
      niceStep = 2
    } else if (normalized <= 5) {
      niceStep = 5
    } else {
      niceStep = 10
    }

    const step = magnitude * niceStep

    return Math.ceil(value / step) * step
  }

  const maxValue = (selectedKey) => {
    const standard = axisMaxMap[selectedKey] || 0
    return getNiceMax(standard)
  }

  // 두 개 다 켜져 있을 때의 Grid 비율 (예: Bar 2슬롯, Boxplot 1슬롯)
  const getGridTemplate = () => {
    if (chartSwitch.bar && chartSwitch.boxplot) return "md:grid-cols-[1fr_minmax(0,430px)]"
    return "grid-cols-1"
  }

  useEffect(() => {
    return () => summaryStore.getState().setSelectedKey("ALL")
  }, [lang, resource])

  return (
    <Wrapper.shadow className={"md:overflow-hidden max-w-full"}>
      <div className="flex justify-between mb-6">
        <h1 className="text-2xl font-bold">
          {formatMessage(statsBundle.totalUsageByHousehold[lang], {
            resource: label[lang]
          })}
        </h1>
        {!isMobile && (
          <div className="flex justify-end">
            <LegendController />
            <ChartController />
          </div>
        )}
      </div>

      {isMobile ? (
        <article className="space-y-10 relative">
          <div className="absolute top-0 right-0 h-full z-5">
            <LegendController />
          </div>

          <BarChart
            className={`min-w-0 min-h-100 h-100`}
            title={formatMessage(statsBundle.usage[lang], {
              resource: label[lang]
            })}
            axisMax={maxValue(selectedKey)}
          />

          <BoxplotVertical
            className={`min-w-0 h-100`}
            title={formatMessage(statsBundle.distribution[lang], {
              resource: label[lang]
            })}
            axisMax={maxValue(selectedKey)}
          />
        </article>
      ) : (
        <article
          className={`grid grid-flow-col transition-all duration-300 h-100 w-full gap-2 ${getGridTemplate()}`}
        >
          {chartSwitch.bar && (
            <BarChart
              className={`min-w-0 h-full overflow-hidden ${!chartSwitch.boxplot ? "col-span-full" : ""}`}
              title={formatMessage(statsBundle.usage[lang], {
                resource: label[lang]
              })}
              axisMax={maxValue(selectedKey)}
            />
          )}

          {chartSwitch.bar && chartSwitch.boxplot && (
            <BoxplotVertical
              className={`min-w-0 h-full overflow-hidden ${!chartSwitch.bar ? "col-span-full" : ""}`}
              title={formatMessage(statsBundle.distribution[lang], {
                resource: label[lang]
              })}
              axisMax={maxValue(selectedKey)}
            />
          )}

          {!chartSwitch.bar && chartSwitch.boxplot && (
            <BoxplotHorizontal
              className={`min-w-0 h-full overflow-hidden ${!chartSwitch.bar ? "col-span-full" : ""}`}
              title={formatMessage(statsBundle.distribution[lang], {
                resource: label[lang]
              })}
              axisMax={maxValue(selectedKey)}
            />
          )}
        </article>
      )}
    </Wrapper.shadow>
  )
}

export default SummaryView
